import React from 'react';
import { motion } from 'framer-motion';
import { 
  Lightbulb, 
  Power, 
  Wifi, 
  AlertTriangle, 
  ShoppingBag, 
  FileCheck,
  CheckCircle2,
  Clock,
  Phone
} from 'lucide-react';
import CTASection from '../components/CTASection';

const ServiceDetailCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}> = ({ icon, title, description, benefits }) => {
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="bg-navy-800 p-6 flex items-center">
        <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mr-4">
          {icon}
        </div>
        <h3 className="text-2xl font-bold text-white">{title}</h3>
      </div>
      <div className="p-6">
        <p className="text-gray-600 mb-6">{description}</p>
        <h4 className="font-semibold text-navy-800 mb-3">Voordelen:</h4>
        <ul className="space-y-2">
          {benefits.map((benefit, index) => (
            <li key={index} className="flex items-start">
              <CheckCircle2 className="text-green-500 mr-2 mt-1 flex-shrink-0" size={18} />
              <span className="text-gray-700">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ServicesPage: React.FC = () => {
  const services = [
    {
      icon: <Lightbulb size={32} className="text-white" />,
      title: 'Lichtinstallaties',
      description:
        'Wij ontwerpen en installeren complete verlichtingssystemen voor woningen en bedrijven. Van basisverlichting tot geavanceerde lichtplannen met dimmers en sensoren, wij zorgen voor de juiste sfeer en functionaliteit.',
      benefits: [
        'Energiezuinige LED-verlichting oplossingen',
        'Sfeervolle verlichting voor elk interieur',
        'Buitenverlichting voor veiligheid en sfeer',
        'Slimme verlichting met sensoren en dimmers',
      ],
    },
    {
      icon: <Power size={32} className="text-white" />,
      title: 'Krachtinstallaties',
      description:
        'Voor machines en apparatuur die meer vermogen vereisen, bieden wij professionele krachtinstallaties. We zorgen voor de juiste aansluiting, veiligheid en functionaliteit van uw krachtstroominstallatie.',
      benefits: [
        'Veilige installatie volgens alle normen',
        'Expertadvies over vermogensbehoeften',
        'Periodieke controle en onderhoud',
      ],
    },
    {
      icon: <Wifi size={32} className="text-white" />,
      title: 'Zwakstroominstallaties',
      description:
        'Wij installeren en onderhouden diverse zwakstroomsystemen zoals netwerken, telefonie, data en intercomsystemen. Zo blijft u altijd verbonden en veilig in uw woning of bedrijf.',
      benefits: [
        'Stabiele netwerkaansluitingen in het hele pand',
        'Intercomsystemen voor toegangscontrole',
        'Gestructureerde data bekabeling voor betrouwbare verbindingen',
      ],
    },
    {
      icon: <AlertTriangle size={32} className="text-white" />,
      title: 'Storingen oplossen',
      description:
        'Elektrische storingen kunnen gevaarlijk zijn en veel ongemak veroorzaken. Wij zijn 24/7 bereikbaar om snel en efficiënt storingen te diagnosticeren en op te lossen, zodat u weer veilig gebruik kunt maken van uw elektriciteit.',
      benefits: [
        'Snelle reactietijd bij noodgevallen',
        '24/7 beschikbaarheid voor storingen',
        'Grondige diagnose van het probleem',
        'Structurele oplossingen in plaats van snelle fixes',
      ],
    },
    {
      icon: <ShoppingBag size={32} className="text-white" />,
      title: 'Verkoop producten',
      description:
        'Naast onze installatiediensten verkopen wij ook hoogwaardige verlichting en installatiemateriaal. We adviseren u graag over de beste producten voor uw specifieke situatie en zorgen voor de juiste materialen.',
      benefits: [
        'Hoogwaardige kwaliteitsproducten',
        'Persoonlijk advies over de beste oplossing',
        'Garantie en after-sales service',
      ],
    },
    {
      icon: <FileCheck size={32} className="text-white" />,
      title: 'Keuringen',
      description:
        'Wij voeren officiële keuringen uit volgens NEN 1010 en NEN 3140 normen. Deze keuringen zijn essentieel voor de veiligheid van uw elektrische installaties en vaak verplicht voor verzekeringen.',
      benefits: [
        'Officiële certificering volgens NEN-normen',
        'Identificatie van potentiële veiligheidsrisico\'s',
        'Advies over noodzakelijke verbeteringen',
        'Keuren electrisch gereedschap volgens NEN 3140 norm',
      ],
    },
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-12 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-4 text-center"
          >
            Onze Diensten
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-center max-w-3xl mx-auto"
          >
            Complete elektrotechnische oplossingen voor particulier en zakelijk
          </motion.p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: 0.1 * index,
                }}
              >
                <ServiceDetailCard
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  benefits={service.benefits}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl font-bold mb-4 text-navy-800"
            >
              Ons Werkproces
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-gray-600 max-w-2xl mx-auto"
            >
              Van eerste contact tot voltooiing van het project, wij houden het proces eenvoudig en transparant
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-navy-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-800">Adviesgesprek</h3>
              <p className="text-gray-600">
                We beginnen met een persoonlijk gesprek om uw wensen en behoeften te begrijpen. Op basis hiervan geven we advies over de beste oplossingen.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-navy-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-800">Offerte</h3>
              <p className="text-gray-600">
                U ontvangt een gedetailleerde, vrijblijvende offerte met duidelijke specificaties en prijzen. Geen verborgen kosten of verrassingen.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-navy-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-800">Uitvoering</h3>
              <p className="text-gray-600">
                Na uw akkoord plannen we de werkzaamheden in en voeren deze uit volgens afspraak. Alles wordt netjes en professioneel afgewerkt.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-navy-600">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-800">Oplevering</h3>
              <p className="text-gray-600">
                We lopen samen het uitgevoerde werk door en geven uitleg over het gebruik. Pas na uw volledige tevredenheid is het project afgerond.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

     

      <CTASection />
    </div>
  );
};

export default ServicesPage;